import { useState, useCallback } from 'react'
import { AudioWaveform, ArrowUpRight, Sparkles } from 'lucide-react'
import { DropZone } from './components/DropZone'
import { AnalysisProgress } from './components/AnalysisProgress'
import { QualityReport } from './components/QualityReport'
import { ShareCard } from './components/ShareCard'
import { analyzeAudio, type ArtifactReport, type AnalysisProgress as ProgressData } from './lib/analyzer'

type AppState = 'landing' | 'analyzing' | 'report'

function App() {
  const [state, setState] = useState<AppState>('landing')
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState<ProgressData>({ step: '', percent: 0 })
  const [report, setReport] = useState<ArtifactReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showShare, setShowShare] = useState(false)

  const handleFileSelect = useCallback(async (selectedFile: File) => {
    setFile(selectedFile)
    setState('analyzing')
    setError(null)

    try {
      const result = await analyzeAudio(selectedFile, setProgress)
      setReport(result)
      setState('report')
    } catch (err) {
      console.error('Analysis failed:', err)
      setError(err instanceof Error ? err.message : 'Analysis failed. Please try again.')
      setState('landing')
    }
  }, [])

  const handleScanAnother = useCallback(() => {
    setFile(null)
    setReport(null)
    setState('landing')
  }, [])

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="border-b border-forge relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[hsl(var(--forge-copper)/0.03)] to-transparent" />
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between relative">
          <button
            onClick={handleScanAnother}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            {/* Forge Logo */}
            <div className="relative">
              <div className="absolute inset-0 bg-[hsl(var(--forge-copper))] blur-xl opacity-30" />
              <div className="relative p-2.5 bg-gradient-to-br from-[hsl(var(--forge-copper))] to-[hsl(var(--forge-copper-dim))] rounded-xl">
                <AudioWaveform className="w-5 h-5 text-[hsl(var(--forge-bg-deep))]" />
              </div>
            </div>
            <div className="text-left">
              <h1 className="text-lg font-semibold tracking-tight">
                <span className="text-forge-copper">Forge</span>
                <span className="text-white ml-1.5">Scanner</span>
              </h1>
              <p className="text-xs text-forge-subtle font-medium tracking-wide uppercase">
                AI Audio Quality Analysis
              </p>
            </div>
          </button>

          <a
            href="https://luminary.tools?utm_source=scanner&utm_medium=header"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 text-sm text-forge-muted hover:text-white bg-forge-elevated hover:bg-forge-surface border border-forge hover:border-forge-bright rounded-lg transition-all group"
          >
            <Sparkles className="w-4 h-4 text-forge-copper" />
            <span>Open Forge</span>
            <ArrowUpRight className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12 relative">
        {/* Hero background - only on landing */}
        {state === 'landing' && (
          <div className="absolute inset-0 overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40"
              style={{ backgroundImage: 'url(/images/hero-bg.png)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--forge-bg-deep))] via-[hsl(var(--forge-bg-deep)/0.8)] to-[hsl(var(--forge-bg-deep)/0.6)]" />
            <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--forge-bg-deep))] via-transparent to-[hsl(var(--forge-bg-deep))]" />
          </div>
        )}

        {state === 'landing' && (
          <div className="w-full max-w-xl mx-auto text-center relative z-10">
            {/* Hero */}
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 text-xs font-medium text-forge-copper bg-[hsl(var(--forge-copper)/0.1)] border border-[hsl(var(--forge-copper)/0.2)] rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--forge-copper))] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[hsl(var(--forge-copper))]"></span>
                </span>
                Free • No signup • Browser-based
              </div>

              <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-5">
                <span className="text-white">Is your AI music</span>
                <br />
                <span className="text-forge-copper">ready for release?</span>
              </h2>

              <p className="text-lg text-forge-muted leading-relaxed max-w-md mx-auto">
                Instant artifact analysis for Suno, Udio, and AI-generated tracks.
                Detect issues before your audience does.
              </p>
            </div>

            {/* Drop zone */}
            <DropZone onFileSelect={handleFileSelect} />

            {/* Error message */}
            {error && (
              <div className="mt-4 p-4 bg-[hsl(var(--forge-danger)/0.1)] border border-[hsl(var(--forge-danger)/0.2)] rounded-xl text-[hsl(var(--forge-danger))] text-sm">
                {error}
              </div>
            )}

            {/* Features grid */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Sizzle', desc: 'High-freq harshness', icon: '◇' },
                { label: 'Muddiness', desc: 'Low-end clarity', icon: '◆' },
                { label: 'Clicks', desc: 'Transient artifacts', icon: '○' },
                { label: 'Loudness', desc: 'LUFS analysis', icon: '●' },
              ].map((feature) => (
                <div
                  key={feature.label}
                  className="forge-card p-4 text-center hover:border-forge-bright transition-colors"
                >
                  <div className="text-forge-copper text-lg mb-2 font-mono">{feature.icon}</div>
                  <p className="text-sm font-medium text-white">{feature.label}</p>
                  <p className="text-xs text-forge-subtle mt-1">{feature.desc}</p>
                </div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="mt-10 flex items-center justify-center gap-6 text-xs text-forge-subtle">
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-forge-success" />
                100% private
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-forge-success" />
                No uploads
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-forge-success" />
                Runs locally
              </span>
            </div>
          </div>
        )}

        {state === 'analyzing' && file && (
          <AnalysisProgress progress={progress} fileName={file.name} />
        )}

        {state === 'report' && report && file && (
          <QualityReport
            report={report}
            file={file}
            onScanAnother={handleScanAnother}
            onShare={() => setShowShare(true)}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-forge py-6">
        <div className="max-w-5xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-forge-subtle">
          <p>
            Built for AI music creators by{' '}
            <a
              href="https://luminary.tools"
              className="text-forge-copper hover:text-[hsl(var(--forge-copper-bright))] transition-colors"
            >
              Forge
            </a>
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="hover:text-forge-muted transition-colors">
              Privacy
            </a>
            <a href="#" className="hover:text-forge-muted transition-colors">
              Terms
            </a>
          </div>
        </div>
      </footer>

      {/* Share modal */}
      {showShare && report && file && (
        <ShareCard
          report={report}
          fileName={file.name}
          onClose={() => setShowShare(false)}
        />
      )}
    </div>
  )
}

export default App
